// THE ANALYZE AND VALUE STEPS' WORDS ARE THE APP'S (WEB-PARITY-4). Tyler, 2026-09-29: "the site should replicate exactly
// what the app has at this time".
//
// Every label, caption, notice and button on `/analyze` (the app's step 4, AnalyzeView) and `/verdict` (its step 5,
// ValueView, with the suggested-value sheet and the "Quick check") that the server does not send is the app's own string,
// copied unchanged from the line it cites (under `AppealAce/`), as `parity3Wording.ts` is for steps 2 and 3. A `{name}` is
// the app's own `\(…)` interpolation; where the app chooses a word inside one (`\(n == 1 ? "sale that" : "sales that")`),
// each word is its own entry on the same line; a sentence the app concatenates across lines is one entry per line,
// joined in order (`.1`, `.2`, …). `test/parity4Wording.test.ts` reads each cited line and fails when the words differ.
//
// THREE OF THE APP'S DEFECTS ARE NOT COPIED (ruling D9, "the app's defects are not copied"), each a removal, never a new
// word:
//   - "CLR" and "STEB", unexplained (`ratioTermShort`, Models.swift:1199; ValueView.swift:1482): the term is written out
//     as the app's own full name for it, "Common Level Ratio" (SubjectPropertyView.swift:2021 calls it the "county's
//     published STEB Common Level Ratio"), and "(STEB)" is left out of :1481;
//   - "Filing an appeal rarely hurts" (SuggestedValue.swift:2534), beside can-raise warnings: the sentence before it
//     stays, that one is left out;
//   - the uniformity sentence's embedded runs of spaces (ValueView.swift:1353-1354): one space where the app's string
//     literal has many (the pin collapses whitespace for that entry).
//
// NOT HERE, ON PURPOSE (each in docs/WEB_READINESS.md's WEB-PARITY-4 section):
//   - the income card (ValueView.swift:997-1181): income is dark on both clients (NEW-20 `notApplicable`);
//   - North Carolina's "Between reappraisals" (ValueView.swift:777-809): no served state is North Carolina, and the base
//     and next years it prints are not on the wire;
//   - the cap-regime card and its just-value field (ValueView.swift:672-762): no served state has a cap regime;
//   - "Case check is a premium step" (AnalyzeView.swift:364-372): every page here is behind the spend;
//   - the dead `dontFileCard` (AnalyzeView.swift:405-496): the app's own page never renders it since D-12's one voice.

import { WEB_PARITY, type AppWording } from "../../components/heldCopy";

function app(appSource: string, text: string): AppWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource };
}

const AV = "Features/Reasons/AnalyzeView.swift";
const VV = "Features/Reasons/ValueView.swift";
const SV = "Features/Reasons/SuggestedValue.swift";
const NCV = "Features/Reasons/NoCaseVerdict.swift";
const NCC = "Features/Reasons/NoCaseVerdictCard.swift";
const VNL = "Features/Reasons/ValueNumberLine.swift";
const LG = "Features/Reasons/LargeGapVerificationView.swift";
const MOD = "Models/Models.swift";
const CS = "Services/CaseStrength.swift";
const MO = "Shared/Motion.swift";

export const PARITY4_WORDING = {
  // ══ /analyze — AnalyzeView (VD-1, VD-2, VD-5 to VD-9, VD-18) ══
  "analyze.card.title": app(`${AV}:254`, "Honest case check"),
  "analyze.stale": app(
    `${AV}:266`,
    "You've changed your comparable sales since this check ran, and re-checks for this appeal are used up. The values below are current; the written summary describes the earlier set.",
  ),
  "analyze.progress.1": app(`${AV}:294`, "Reading your comparable sales…"),
  "analyze.progress.2": app(`${AV}:295`, "Comparing each one to your home…"),
  "analyze.progress.3": app(`${AV}:296`, "Scoring the closest matches…"),
  "analyze.progress.4": app(`${AV}:297`, "Weighing your strongest evidence…"),
  "analyze.longRun": app(`${AV}:303`, "Still working — this one has more to weigh than most. Keep the app open."),
  "analyze.run": app(`${AV}:311`, "Analyze my comps"),
  "analyze.run.sub": app(`${AV}:313`, "See which comps best support a lower value"),
  "analyze.error": app(`${AV}:657`, "Couldn't run the check right now. Try again."),
  "analyze.limit": app(`${AV}:573`, "You've hit the evaluation limit for this appeal."),
  "analyze.needComps": app(`${AV}:331`, "Add your assessed value and at least one comp first."),
  "analyze.footer.acquisition": app(
    `${AV}:348`,
    "California's Prop 8 test compares recent sales to your assessed value alone — condition and photos can't create a case here.",
  ),
  "analyze.footer.avm": app(
    `${AV}:350`,
    "An independent estimate of your home's value — not comps or condition — is what settles this one.",
  ),
  "analyze.footer.recommendation": app(
    `${AV}:358`,
    "Comps can't see condition or a wrong record — that's why the recommendation above turns on whether you have either.",
  ),
  "analyze.footer.default": app(
    `${AV}:360`,
    "If the comps alone don't tell the full story, the condition and photos you added round it out.",
  ),
  "analyze.continue": app(`${AV}:209`, "Continue to value"),

  // The VerdictCard (AnalyzeView.swift:656-905).
  "verdict.assessed": app(`${AV}:279`, "Assessed"),
  "verdict.assessedMarket": app(`${AV}:279`, "Assessed (market)"),
  "verdict.compsImplyAbout": app(`${AV}:801`, "Comps imply about {low}–{high}"),
  "verdict.strongest.head": app(`${AV}:736`, "Your {count} strongest comp{plural}"),
  "verdict.strongest.plural": app(`${AV}:736`, "s"),
  "verdict.strongest.size": app(`${AV}:740`, ": {size} sq ft home{plural}"),
  "verdict.strongest.sizeRange": app(`${AV}:739`, "{low}–{high}"),
  "verdict.strongest.sold": app(`${AV}:744`, " that sold "),
  "verdict.strongest.priceRange": app(`${AV}:744`, "{low}–{high}"),
  "verdict.strongest.end": app(`${AV}:746`, "."),
  "verdict.quality": app(`${AV}:766`, "{strength} · {precision}"),
  "verdict.recommend": app(`${AV}:839`, "What we recommend"),
  "verdict.weakCase": app(`${AV}:873`, "This looks like a weak case"),
  "verdict.title.strongCase": app(`${MOD}:2951`, "Your comps support a lower value"),
  "verdict.title.possibleCase": app(`${MOD}:2952`, "Your comps suggest the assessment may be high"),
  "verdict.title.borderline": app(`${MOD}:2953`, "Your comps land close to your assessment"),
  "verdict.title.inconclusive": app(`${MOD}:2954`, "It looks like the evidence says you're in good shape"),
  "verdict.title.insufficientData": app(`${MOD}:2955`, "Add a few more comps to firm up the case"),
  "verdict.title.noCaseAcquisitionValue": app(`${MOD}:2956`, "There's no case to file — your assessment is already protected"),
  "verdict.title.noCaseAvmCorroborated": app(`${MOD}:2957`, "An independent estimate says you may already be fairly assessed"),
  "verdict.rec.borderline.1": app(`${MOD}:3017`, "Comparable sales alone won't carry this one. It's worth filing if you have "),
  "verdict.rec.borderline.2": app(`${MOD}:3018`, "condition problems to document, a wrong detail on your record, or a nearby "),
  "verdict.rec.borderline.3": app(`${MOD}:3019`, "sale we didn't find — otherwise we'd leave it."),
  "verdict.rec.inconclusive.1": app(`${MOD}:3021`, "On the sales we found, your assessment holds up — that's a good result. We "),
  "verdict.rec.inconclusive.2": app(`${MOD}:3022`, "don't recommend appealing unless you have condition photos to add, a wrong "),
  "verdict.rec.inconclusive.3": app(`${MOD}:3023`, "detail on your record, or a nearby sale we missed."),
  "verdict.rec.insufficientData.1": app(`${MOD}:3028`, "We couldn't find enough usable sales to judge this yet. Add any nearby sales "),
  "verdict.rec.insufficientData.2": app(`${MOD}:3029`, "you know of and we'll take another look."),
  "verdict.rec.noCaseAcquisitionValue.1": app(`${MOD}:3031`, "There's no reduction to win here, so we don't recommend filing."),
  "verdict.rec.noCaseAvmCorroborated.1": app(`${MOD}:3033`, "An independent estimate agrees with your assessment. We don't recommend "),
  "verdict.rec.noCaseAvmCorroborated.2": app(`${MOD}:3034`, "appealing unless you have condition photos to add or a wrong detail on your "),
  "verdict.rec.noCaseAvmCorroborated.3": app(`${MOD}:3035`, "record."),

  // The value number line (ValueNumberLine.swift).
  "line.compsImply": app(`${VNL}:76`, "Comps imply"),
  "line.gap": app(`${VNL}:135`, "−{amount} · −{percent}%"),
  "line.a11y": app(
    `${VNL}:91`,
    "{caption} {assessed}. Comps imply {low} to {high}. A gap of {amount}, about {percent} percent lower.",
  ),

  // The precision tier's own words (SuggestedValue.swift:79-88) and the meter's noun (CaseStrength.swift:42-44).
  "precision.high": app(`${SV}:85`, "High precision"),
  "precision.medium": app(`${SV}:86`, "Medium precision"),
  "precision.low": app(`${SV}:87`, "Lower precision"),
  "meter.headline": app(`${CS}:43`, "{label} reduction"),

  // "What the wider neighborhood says" (AnalyzeView.swift:505-535).
  "hood.title": app(`${AV}:513`, "What the wider neighborhood says"),
  "hood.counts": app(
    `${AV}:516`,
    "Of the {total} recent nearby sales of homes like yours, **{below} sold for less** per square foot than the county values yours at, and **{above} sold for more**.",
  ),
  "hood.basis": app(
    `${AV}:519`,
    "Your assessment works out to about {rate}/sq ft. This counts every clean sale we found nearby — not just the closest twelve we use to price your home — so it can come out either way.",
  ),
  "hood.mostlyBelow": app(
    `${AV}:524`,
    "Most of the sales around you sold for less per square foot than the county values yours at.",
  ),
  "hood.roughlyEven": app(
    `${AV}:528`,
    "The sales around you split roughly evenly above and below where the county has you. That doesn't make an appeal impossible, but it does mean the sales alone aren't your argument — condition, or a sale we don't have, would be.",
  ),
  "hood.mostlyAtOrAbove": app(
    `${AV}:532`,
    "Most of the sales around you are at or above where the county has you. That doesn't make an appeal impossible, but it does mean the sales alone aren't your argument — condition, or a sale we don't have, would be.",
  ),

  // "Comps alone won't carry this" (AnalyzeView.swift:540-568).
  "strengthen.title": app(`${AV}:543`, "Comps alone won't carry this"),
  "strengthen.body": app(
    `${AV}:546`,
    "Your comparable sales don't support a lower value on their own. The strongest next move is to document condition and damage — a worn roof, foundation cracks, water damage — the kind of thing comps don't capture but a board weighs heavily.",
  ),
  "strengthen.addSale": app(
    `${AV}:554`,
    "If you know of a sale nearby that isn't in our list — a neighbour's house, something that sold rough or sold quietly — add it. We only see what the records carry, and you know your own block better than they do.",
  ),
  "strengthen.back": app(`${AV}:557`, "Go back to add condition & photos"),
  "strengthen.note": app(`${AV}:566`, "Your comps stay loaded — nothing re-runs."),

  // The one-voice no-case page (NoCaseVerdict.swift, NoCaseVerdictCard.swift; VD-8, VD-9).
  "nocase.result.belowMarket": app(`${NCV}:78`, "{gap} below what the sales say"),
  "nocase.result.recentPurchase": app(`${NCV}:80`, "Confirmed by your own sale"),
  "nocase.verdict.belowMarket": app(`${NCV}:93`, "The sales near you support MORE than {basis}, not less."),
  "nocase.verdict.recentPurchase": app(`${NCV}:95`, "You bought this home at or above the {basis} your assessment already implies."),
  "nocase.evidence.belowMarket.1": app(`${NCV}:111`, "We pulled {count} nearby sales and adjusted each one to your home for "),
  "nocase.evidence.belowMarket.2": app(`${NCV}:112`, "size, age and distance before comparing. Only sales that survived that "),
  "nocase.evidence.belowMarket.3": app(`${NCV}:113`, "screening count toward the figures above."),
  "nocase.evidence.recentPurchase.1": app(`${NCV}:117`, "An arm's-length purchase is the strongest evidence of value there is — it is "),
  "nocase.evidence.recentPurchase.2": app(`${NCV}:118`, "what the market actually paid, not an estimate of it. The nearby sales agree."),
  "nocase.limitation": app(`${NCV}:124`, "This is a data check against the sales we can see, not an appraisal."),
  "nocase.metric.sourced": app(`${NCV}:180`, "sales sourced"),
  "nocase.metric.midpoint": app(`${NCV}:181`, "adjusted midpoint"),
  "nocase.metric.above": app(`${NCV}:182`, "above your basis"),
  "nocase.metric.aboveValue": app(`${NCV}:182`, "+{percent}%"),
  "nocase.metric.paid": app(`${NCV}:192`, "you paid"),
  "nocase.metric.paidNone": app(`${NCV}:192`, "—"),
  "nocase.metric.when": app(`${NCV}:193`, "when you bought"),
  "nocase.metric.whenUnknown": app(`${NCV}:216`, "recently"),
  "nocase.savesYou": app(`${NCC}:83`, "WHAT THIS SAVES YOU"),
  "nocase.wouldChange": app(`${NCC}:98`, "WHAT WOULD CHANGE THIS"),
  "nocase.finish": app(`${NCV}:243`, "Save this and finish"),
  "nocase.continue": app(`${NCV}:244`, "Continue anyway — I know something these records don't"),
  "nocase.continueNote": app(`${NCV}:256`, "These are the sales your letter would cite."),

  // ══ /verdict — ValueView (VD-10 to VD-18, AN-2) ══
  "value.hero.title": app(`${VV}:231`, "Your requested value"),
  "value.hero.acquisitionNoCase": app(
    `${VV}:200`,
    "An independent check suggests you may already be fairly assessed, so there's no numeric value to enter here. If you have condition or record-error grounds, pick them below and we'll build a case around those instead.",
  ),
  "value.hero.counted": app(
    `${VV}:219`,
    "{count} of your {usable} sales adjust to below your assessment — we need at least 3 before we'll put a number behind an opinion of value, so we're not going to invent one. Enter what you believe the home is WORTH below, and we'll build the case on the grounds you pick.",
  ),
  "value.hero.counted.none": app(`${VV}:219`, "None"),
  "value.hero.counted.only": app(`${VV}:219`, "Only {supporting}"),
  "value.hero.noSuggestion": app(
    `${VV}:221`,
    "Your comps didn't point to a lower value on their own, so enter what you believe the home is WORTH below — we'll build the case on the grounds you pick.",
  ),
  "value.hero.suggestion": app(
    `${VV}:223`,
    "Here's the value your comps support, and the grounds we'll cite. Confirm or adjust both before we build the letter.",
  ),
  "value.tooLow.title": app(`${VV}:131`, "Assessed value looks too low"),
  "value.tooLow.body": app(
    `${VV}:133`,
    "Your comparable sales are far above your {assessed} assessed value. That usually means the figure pulled from public records is partial or wrong — and the whole comparison keys off it. Fixing it is free: your comps are saved, so nothing re-runs.",
  ),
  "value.tooLow.fix": app(`${VV}:138`, "Go back and fix the value"),
  "value.outliers.title": app(`${VV}:156`, "Set aside as outliers"),
  "value.outliers.footer": app(
    `${VV}:158`,
    "Left out of your value so one off sale can't drag it down. Keep one only if you know it's a genuine match.",
  ),
  "value.outliers.unnamed": app(`${VV}:161`, "Comparable sale"),
  "value.outliers.keep": app(`${VV}:167`, "Keep this comp anyway"),

  // The "Requested value" card (ValueView.swift:354-513).
  "value.card.title": app(`${VV}:368`, "Requested value"),
  "value.card.footer": app(
    `${VV}:369`,
    "What you're asking the board to assess your home at — derived from your comps. Tap to see the math or change it.",
  ),
  "value.purchase.1": app(`${VV}:380`, "You bought this home for {purchase}"),
  "value.purchase.in": app(`${VV}:381`, " in {when}"),
  "value.purchase.2": app(
    `${VV}:382`,
    ". This request is {percent}% below that. A board will weigh your own purchase heavily — if the home is genuinely worth less now, say why in the condition step; otherwise consider asking closer to what you paid.",
  ),
  "value.requesting.eyebrow": app(`${VV}:399`, "You're requesting"),
  "value.requesting.off": app(`${VV}:402`, "{amount} off"),
  "value.requesting.basis": app(`${VV}:411`, "off your **assessed** value — −{percent}%"),
  "value.row.suggestedMarket": app(`${VV}:429`, "Suggested from your comps · market value"),
  "value.row.suggested": app(`${VV}:429`, "Suggested from your comps"),
  "value.row.requestingMarket": app(`${VV}:430`, "Requesting · market value"),
  "value.row.requesting": app(`${VV}:430`, "Requesting"),
  "value.row.review": app(`${VV}:441`, "Review"),
  "value.row.change": app(`${VV}:441`, "Change"),
  "value.stopped.title": app(`${VV}:464`, "Why we stopped here"),
  "value.stopped.body": app(
    `${VV}:466`,
    "{address} sold nearby and works out to about {value} for a home your size{others}. Sales like that pull against a bigger cut, so we've set this as the most we'd ask for rather than the most the lower sales alone would support.",
  ),
  "value.stopped.others": app(`${VV}:466`, ", and {count} other nearby {verb} the same"),
  "value.stopped.others.one": app(`${VV}:466`, "sale does"),
  "value.stopped.others.many": app(`${VV}:466`, "sales do"),
  "value.stopped.lead": app(
    `${VV}:477`,
    "Your letter argues from the sales that support you — it doesn't raise this one. Expect the board to know about it.",
  ),
  "value.stopped.noLead": app(`${VV}:478`, "Your letter doesn't raise this sale. Expect the board to know about it."),
  "value.capped.title": app(`${VV}:490`, "Capped at what you paid"),
  "value.capped.body": app(
    `${VV}:492`,
    "You bought this property for {price}{when}. We won't ask the board for more than that — asking above your own purchase price leaves money on the table and invites the obvious question. That's why this figure sits where it does.",
  ),
  "value.capped.in": app(`${VV}:492`, " in {when}"),
  "value.capped.lead": app(`${VV}:495`, "It's also the strongest evidence you have. Your letter leads with it."),
  "value.submarket": app(
    `${VV}:507`,
    "Your comps span very different price levels per square foot — likely different submarkets. We've widened the range and lowered confidence to reflect that.",
  ),
  "value.manual.footerMarket": app(
    `${VV}:560`,
    "Enter what the home is WORTH. Your county assesses at a percentage of that, so the figure on your notice is the smaller one — we convert it for you below and ask the Board for that.",
  ),
  "value.manual.footer": app(`${VV}:561`, "What you believe it should be assessed at."),
  "value.manual.labelMarket": app(`${VV}:614`, "What is your home worth on the open market?"),
  "value.manual.label": app(`${VV}:615`, "What should it be assessed at?"),
  "value.manual.convert": app(
    `${VV}:621`,
    "We'll ask the Board to enter {assessed} assessed — that's {market} at the county's {pct} {term}.",
  ),
  "value.noCase.title": app(`${VV}:529`, "No case to file"),
  "value.noCase.footer": app(
    `${VV}:531`,
    "If you know of a recent sale below your assessed value that wasn't found, add it as a comp and we'll re-check.",
  ),
  "value.noCase.body": app(
    `${VV}:532`,
    "There isn't a number to request here — a Prop 8 appeal only exists when comps sit below your assessed value, and yours don't.",
  ),
  // `ratioTermShort` (Models.swift:1197-1204), "CLR" written out as the app's own full name (D9; see the header).
  "term.pa": app("Features/Subject/SubjectPropertyView.swift:2021", "Common Level Ratio"),
  "term.nj": app(`${MOD}:1200`, "Director's Ratio"),
  "term.nh": app(`${MOD}:1201`, "equalization ratio"),
  "term.other": app(`${MOD}:1202`, "assessment ratio"),
  "note.assessedEquivalent": app(`${MOD}:1214`, "≈ {assessed} assessed at the county's {pct} {term}"),

  // "Adjust for condition (optional)" (ValueView.swift:813-956; VD-14).
  "condition.title": app(`${VV}:817`, "Adjust for condition (optional)"),
  "condition.footer": app(
    `${VV}:819`,
    "Optional. Uses the condition and photos you documented to adjust only for what's WORSE than the comps already reflect — the market-recognized impact, not a repair bill. Every line shown.",
  ),
  "condition.progress.1": app(`${VV}:827`, "Reading your condition notes…"),
  "condition.progress.2": app(`${VV}:828`, "Pricing each issue vs. the comps…"),
  "condition.progress.3": app(`${VV}:829`, "Keeping only market-recognized impact…"),
  "condition.run": app(`${VV}:835`, "Adjust for condition"),
  "condition.error": app(`${VV}:974`, "Couldn't analyze condition right now. Try again."),
  "condition.none": app(
    `${VV}:854`,
    "No condition adjustment — what you documented reads as normal wear, so the comp value stands. That's the honest call.",
  ),
  "condition.lowConfidence": app(`${VV}:866`, "Low confidence"),
  "condition.reduction": app(`${VV}:873`, "−{amount}"),
  "condition.adjustedMarket": app(`${VV}:880`, "Condition-adjusted market value"),
  "condition.adjusted": app(`${VV}:881`, "Condition-adjusted value"),
  "condition.capped": app(
    `${VV}:887`,
    "We scaled these back to a conservative share of the comp value — a built-in sanity limit, not a legal cap. A formal appraisal can document more if the condition warrants it.",
  ),
  "condition.applied": app(`${VV}:900`, "Applied as your requested value ✓"),
  "condition.use": app(`${VV}:901`, "Use {amount} as my requested value"),
  "condition.marketNote": app(
    `${VV}:931`,
    "That's a MARKET value. At the county's {pct} {term} it becomes {assessed} on your notice — that's the figure the Board would enter.",
  ),
  "condition.method.costToCure": app(`${VV}:951`, "Cost to cure"),
  "condition.method.ageLife": app(`${VV}:952`, "Age/life"),
  "condition.method.market": app(`${VV}:953`, "Market"),

  // "Check uniformity (optional)" (ValueView.swift:1242-1516; VD-13).
  "uniformity.title": app(`${VV}:1246`, "Check uniformity (optional)"),
  "uniformity.footer": app(
    `${VV}:1248`,
    "A second angle: are you assessed HIGHER than comparable nearby properties? In {state} that alone can support a reduction — separate from what your home is worth.",
  ),
  "uniformity.state.tx": app(`${VV}:1496`, "Texas"),
  "uniformity.state.nj": app(`${VV}:1497`, "New Jersey"),
  "uniformity.state.other": app(`${VV}:1498`, "your state"),
  "uniformity.progress.1": app(`${VV}:1256`, "Pulling nearby parcel assessments…"),
  "uniformity.progress.2": app(`${VV}:1257`, "Comparing your assessment to the neighborhood…"),
  "uniformity.progress.3": app(`${VV}:1258`, "Weighing the equity case…"),
  "uniformity.run": app(`${VV}:1264`, "Check my assessment vs. the neighborhood"),
  "uniformity.meaning.corridor": app(
    `${VV}:1509`,
    "**What this means:** your county assesses at a published ratio of market value. Your assessment divided by your home's market value comes out **higher than that published ratio**, so the assessment should be corrected to market value × the ratio. Note this argument **depends on the market value used** — it's a different route to relief than your comps, but it starts from the same number.",
  ),
  "uniformity.meaning.parcels": app(
    `${VV}:1511`,
    "**What this means:** even if your home's market value were fair, you can still be over-taxed when you're assessed **higher per square foot than similar homes nearby**. Yours is — so this is a valid *second* argument, judged on fairness between neighbors rather than on what your home is worth. Boards can grant it even when sales evidence is thin.",
  ),
  "uniformity.contested": app(
    `${VV}:1326`,
    "This rests on your comps' market value. An automated third-party estimate of {estimate} would put you inside the county's range, where this ground doesn't apply — so treat it as contested, not settled. It's a computer-generated estimate, not an appraisal, and it's shown because a board may raise it.",
  ),
  "uniformity.ratio": app(`${VV}:1337`, "Your ratio: **{ratio}** · county range {low}–{high}"),
  "uniformity.implausible": app(
    `${VV}:1354`,
    "This is a very large gap — it says the county assessed you at well over 1.5× what your comps say the home is worth. That happens, but the more common cause is comps that aren't really like your home: a different era of building, a different block, a different kind of unit. Check your comps before filing on this.",
  ),
  "uniformity.added": app(`${VV}:1416`, "Uniformity added as a supporting ground"),
  "uniformity.useLower": app(`${VV}:1417`, "Use this lower equity value · {equity}"),
  "uniformity.add": app(`${VV}:1418`, "Add uniformity as a supporting ground"),
  "uniformity.remove": app(`${VV}:1450`, "Remove this ground from my appeal"),
  "uniformity.assessedNote": app(
    `${VV}:1469`,
    "That's an assessed value — about {market} in market terms at the county's {pct} level.",
  ),
  "uniformity.basis.corridor": app(
    `${VV}:1482`,
    "Derived from your county's published Common Level Ratio, not a sample of parcels. This is an equity argument — verify your county's process before filing.",
  ),
  "uniformity.basis.parcels": app(
    `${VV}:1484`,
    "Based on {count} comparable assessed parcels. This is an equity argument — verify your county's process before filing.",
  ),
  "uniformity.basis.none": app(`${VV}:1486`, "This is an equity argument — verify your county's process before filing."),
  "independent.withRange": app(`${MOD}:1428`, "around {estimate} (roughly {low}–{high}; {caveat})"),
  "independent.plain": app(`${MOD}:1430`, "around {estimate} ({caveat})"),
  "independent.caveat": app(`${MOD}:1426`, "an automated valuation, not a formal appraisal"),

  // The case-strength card (ValueView.swift:1577-1631; Motion.swift:52; VD-15).
  "strength.title": app(`${MO}:52`, "Case strength"),

  // "Grounds for your appeal" (ValueView.swift:1651-1725; Models.swift:174-201; VD-11).
  "grounds.title": app(`${VV}:1655`, "Grounds for your appeal"),
  "grounds.footer": app(`${VV}:1656`, "We pre-selected these from what you've given us. Tap any to add or remove it."),
  "reason.overvaluation": app(`${MOD}:178`, "Overvaluation"),
  "reason.comparableSales": app(`${MOD}:179`, "Comparable sales are lower"),
  "reason.conditionIssues": app(`${MOD}:180`, "Condition / damage issues"),
  "reason.factualError": app(`${MOD}:181`, "Factual record error"),
  "reason.overvaluation.blurb": app(`${MOD}:190`, "The assessed value exceeds the property's true market value."),
  "reason.comparableSales.blurb": app(`${MOD}:192`, "Recent sales of similar nearby homes support a lower value."),
  "reason.conditionIssues.blurb": app(`${MOD}:194`, "Deferred maintenance, damage, or defects reduce the value."),
  "reason.factualError.blurb": app(`${MOD}:196`, "The county record has an error (sqft, beds, baths, lot, etc.)."),
  "grounds.nudge.title": app(`${VV}:1687`, "Add condition evidence so this ground holds up"),
  "grounds.nudge.body": app(
    `${VV}:1689`,
    "You selected condition / damage, but haven't added any photos or notes yet — the letter can't argue condition without them.",
  ),
  "grounds.nudge.back": app(`${VV}:1694`, "Add photos & condition notes"),
  "grounds.record.label": app(`${VV}:1707`, "What's incorrect in the county record?"),
  "grounds.record.placeholder": app(`${VV}:1709`, "e.g., Record lists 1,200 sq ft; the home is 950"),
  "grounds.record.empty": app(
    `${VV}:1718`,
    "Add the specifics so the letter can state the exact error — we only cite what you write here, and never invent details.",
  ),
  "grounds.record.filled": app(`${VV}:1719`, "We'll present this as your reported correction for the board to verify."),
  "value.pickGround": app(`${VV}:275`, "Pick at least one ground for your appeal."),
  "value.continue": app(`${VV}:279`, "Continue to generate"),

  // ══ The suggested-value sheet (SuggestedValueSheet, SuggestedValue.swift:2227-2577; VD-10, VD-16) ══
  "sheet.title": app(`${SV}:2371`, "Your requested value"),
  "sheet.cancel": app(`${SV}:2376`, "Cancel"),
  "sheet.eyebrowMarket": app(`${SV}:2291`, "SUGGESTED REQUESTED MARKET VALUE"),
  "sheet.eyebrow": app(`${SV}:2291`, "SUGGESTED REQUESTED VALUE"),
  "sheet.below": app(`${SV}:2316`, "≈ {amount} ({percent}) below your {basis} {label}"),
  "sheet.below.percent": app(`${SV}:2315`, "{percent}%"),
  "sheet.below.underOne": app(`${SV}:2315`, "<1%"),
  "sheet.basis.market": app(`${SV}:2281`, "implied market value"),
  "sheet.basis.assessment": app(`${SV}:2281`, "assessment"),
  "sheet.range.title": app(`${SV}:2430`, "Supported range"),
  "sheet.range": app(`${SV}:2432`, "{low} – {high}"),
  "sheet.tempered": app(
    `${SV}:2439`,
    "Your comps point to about {indicated}, but on {word} evidence we recommend requesting {value} — a more defensible ask the board is likelier to grant. You can still request lower below.",
  ),
  "sheet.untempered": app(
    `${SV}:2443`,
    "Your comps support a value in this range; we target {value} — toward the well-supported low end, never below your lowest defensible comp.",
  ),
  "sheet.word.high": app(`${SV}:2414`, "strong"),
  "sheet.word.medium": app(`${SV}:2415`, "moderate"),
  "sheet.word.low": app(`${SV}:2416`, "thin"),
  "sheet.precisionNote": app(
    `${SV}:2461`,
    "This is precision — how tightly your comps pin the exact number. It's a different axis from case strength (how large and well-supported your reduction is), so a strong case can still show medium precision.",
  ),
  "sheet.weak": app(
    `${SV}:2470`,
    "This rests on {comps}. Adding one or two more — or documenting condition — would carry far more weight with the board.",
  ),
  "sheet.weak.one": app(`${SV}:2470`, "a single comparable"),
  "sheet.weak.many": app(`${SV}:2470`, "only {count} comparables"),
  "sheet.reach": app(
    `${SV}:2480`,
    "The closest sales that support a lower value are outside your immediate area. Boards weigh nearby sales most, so treat this as a starting point — a comparable sale closer to your home would make the case stronger.",
  ),
  "sheet.how.title": app(`${SV}:2497`, "How we got this"),
  "sheet.how.fractional": app(
    `${SV}:2501`,
    "Your county assesses at about **{pct} of market value**, so we compare your comps to your implied market value of **{basis}** — not the lower assessed figure.",
  ),
  "sheet.how.derivedBelowThat": app(`${SV}:2502`, "This figure is derived from {count} comparable {sales} sold below that."),
  "sheet.how.derivedBelow": app(`${SV}:2506`, "This figure is derived from {count} comparable {sales} sold below your {basis} assessment."),
  "sheet.how.saleThat": app(`${SV}:2506`, "sale that"),
  "sheet.how.salesThat": app(`${SV}:2506`, "sales that"),
  "sheet.how.sizeRate": app(`${SV}:2508`, "Adjusted to your home's size, that's about **{rate}/sq ft**."),
  "sheet.how.tempered": app(
    `${SV}:2511`,
    "On {word} evidence we don't recommend asking for the full cut — so we suggest **{value}** instead of {indicated}, a number that's far easier to defend.",
  ),
  "sheet.how.floored": app(
    `${SV}:2517`,
    "Your comps alone would imply a steeper cut than we recommend showing — we capped it at 50% of your assessment as a conservative ceiling, so this figure is more conservative than the comps, not less.",
  ),
  "sheet.how.equity": app(
    `${SV}:2524`,
    "Your comps point to **{value}**, and the uniformity ground you adopted supports **{floor}** (about {floorAssessed} assessed). We request the lower of the two — the stronger ask — so that's the number this button applies.",
  ),
  "sheet.how.moreComps": app(`${SV}:2528`, "More comps or documented condition would let you push closer to the comp-indicated value."),
  "sheet.how.documented": app(`${SV}:2529`, "Requesting it gives the board a documented, comp-backed number."),
  "sheet.how.thin": app(
    `${SV}:2534`,
    "There wasn't enough comp detail to derive a value, so we suggest a modest reduction.",
  ),
  "sheet.how.target": app(`${SV}:2538`, "This sets the target the letter argues for — you can change it anytime."),
  "sheet.size.withSqft": app(`${SV}:2565`, "Applied to your ~{sqft} sq ft home, that points to"),
  "sheet.size.noSqft": app(`${SV}:2567`, "For a home your size, that points to"),
  "sheet.size.fractional": app(
    `${SV}:2573`,
    "{prefix} a market value of about **{market}** — an assessed value of roughly **{assessed}** at your county's {pct} {term}.",
  ),
  "sheet.size.plain": app(`${SV}:2575`, "{prefix} roughly **{indicated}**."),
  "sheet.own.title": app(`${SV}:2326`, "Or set your own amount"),
  "sheet.own.labelMarket": app(`${SV}:2331`, "Request this MARKET value"),
  "sheet.own.label": app(`${SV}:2331`, "Request this value"),
  "sheet.own.belowAssessed": app(
    `${SV}:2341`,
    "That's below your {assessed} assessed value — enter the MARKET value, not the assessed one.",
  ),
  "sheet.own.askBelow": app(
    `${SV}:2358`,
    "Asking below {value} changes which sales support you: some of your comps sold above this number, so the letter will present them as an upper bound rather than as support. Fewer sales will be cited as backing the figure.",
  ),
  "sheet.own.askAbove": app(
    `${SV}:2359`,
    "Asking above {value} means more of your sales sit below the request, so the letter can cite more of them — but a board is likelier to grant a number the evidence clearly reaches.",
  ),
  "sheet.use": app(`${SV}:2387`, "Use {amount}"),

  // ══ The "Quick check" (LargeGapVerificationView.swift; VD-12) ══
  "lg.title": app(`${LG}:78`, "Quick check"),
  "lg.cancel": app(`${LG}:82`, "Cancel"),
  "lg.heading": app(`${LG}:141`, "Let's confirm your home's details"),
  "lg.gap": app(
    `${LG}:151`,
    "Your comps point to a value about {percent}% below what the county has. That's a larger reduction than most appeals ask for",
  ),
  "lg.gap.mismatch": app(
    `${LG}:153`,
    "{gap}, so it's worth confirming two things before we file: that the county's record of your home is accurate, and that these sales are really comparable to your home.",
  ),
  "lg.gap.record": app(
    `${LG}:155`,
    "{gap}, so before we file we want to be sure the county's record of your home — its square footage especially — is accurate. If something in that record is wrong, that's often the strongest version of this case.",
  ),
  "lg.size.title": app(`${LG}:162`, "Size check"),
  "lg.size.one": app(`${LG}:166`, "{sqft} sq ft"),
  "lg.size.range": app(`${LG}:167`, "{low}–{high} sq ft"),
  "lg.size.line": app(`${LG}:168`, "Your comps are {range}.{home}"),
  "lg.size.home": app(`${LG}:168`, " Your home is {sqft} sq ft."),
  "lg.size.within": app(`${LG}:176`, "That's within your comps' range."),
  "lg.record.title": app(`${LG}:190`, "Does the county's record of your home look right?"),
  "lg.record.sub": app(`${LG}:193`, "Square footage, bedrooms, bathrooms — as far as you know."),
  "lg.record.yes": app(`${LG}:197`, "Yes, it looks right"),
  "lg.record.no": app(`${LG}:200`, "No, something's off"),
  "lg.mismatch": app(
    `${LG}:216`,
    "Your home's size is far outside the range of your comps. Every sale is adjusted to your home's square footage, so the difference is accounted for — but a sale closer to your size is harder for a board to argue with. You can step back to the comps and add one, or continue.",
  ),
  "lg.fixFirst": app(`${LG}:88`, "Let's fix the county's record first — that's the strongest version of this case."),
  "lg.canContinue": app(
    `${LG}:91`,
    "You can continue — size differences are adjusted for. A sale closer to your home's size would still make the case stronger.",
  ),
  "lg.fix": app(`${LG}:239`, "Fix the record"),
  "lg.continue": app(`${LG}:240`, "This looks right — continue"),
  "lg.reason.mismatch": app(
    `${LG}:268`,
    "The county's record of the home has been confirmed as accurate. The nearest available comparable sales differ from the home in size, and each has been adjusted to the home's square footage on the attached exhibit.",
  ),
  "lg.reason.match": app(
    `${LG}:270`,
    "The comparable sales closely match your home in size and type — the large gap reflects the market evidence, not a mismatch in the comps.",
  ),
} as const satisfies Record<string, AppWording>;

export type Parity4WordingId = keyof typeof PARITY4_WORDING;

/** THE D9 EDITS (see the header), each the app's literal with exactly this taken out — the pin checks the edit, so an
 *  entry cannot drift into new words under the D9 label. `collapse` reads the app's literal with its runs of spaces made
 *  one; `cut` names the text removed; `within` is a run of the app's literal, quoted from a longer one. */
export const D9_EDITS: Readonly<Partial<Record<Parity4WordingId, { collapse?: true; cut?: string; within?: true }>>> = {
  "term.pa": { within: true },
  "uniformity.basis.corridor": { cut: " (STEB)" },
  "uniformity.implausible": { collapse: true },
  "sheet.how.thin": { cut: " Filing an appeal rarely hurts — you can always set your own amount." },
  // NOT A D9 DEFECT, THE SAME KIND OF CUT: the limit's number. Tyler, 2026-09-29, on the website: the number of
  // iterations a credit buys is not stated (row 29 of `test/thirtyThreeDecisions.test.tsx` holds every screen to reading
  // no cap to print it). The sentence still says why the check cannot run.
  "analyze.limit": { cut: " ({})" },
};

export function say4(id: Parity4WordingId): string {
  return PARITY4_WORDING[id].text;
}

/** A sentence with its `{name}` slots filled. A slot left unfilled is a thrown error, never printed braces. */
export function say4With(id: Parity4WordingId, values: Readonly<Record<string, string | number>>): string {
  return say4(id).replace(/\{(\w+)\}/g, (_, name: string) => {
    if (!Object.hasOwn(values, name)) throw new Error(`say4With(${id}): no value for {${name}}`);
    return String(values[name]);
  });
}

/** A sentence the app concatenates across lines (`.1`, `.2`, …), joined in order. */
export function say4Joined(prefix: string, values: Readonly<Record<string, string | number>> = {}): string {
  const parts = (Object.keys(PARITY4_WORDING) as Parity4WordingId[]).filter((id) => /\.\d+$/.test(id) && id.slice(0, id.lastIndexOf(".")) === prefix);
  if (parts.length === 0) throw new Error(`say4Joined(${prefix}): no parts`);
  return parts.map((id) => say4With(id, values)).join("");
}
